// Mouse drag scrolling for hooks with a horizontally scrolling, snapping
// element (touch and pen already swipe natively): dragging past either end
// stretches it, and on release it snaps to the nearest item.
//
// Spread `dragScroll` into the hook, add `dragScrollActions(target)` to its
// `actions` (`target` names the scrolling element's target, none for the hook
// root) and implement:
// - `dragScroller()`: the scrolling element
// - `dragItems()`: its snapping items
// - `dragStateClass(state)`: the scrolling element's class for the `dragging`
//   (button held) and `snapping` (after release, snapping still off) states
//
// The scrolling element's CSS must turn snapping off in both states and apply
// the stretch with `translate: var(--overscroll, none)`, transitioned except
// while dragging so it eases back on release.

// px the mouse has to move before a press turns into a drag, so clicks still work
const DRAG_THRESHOLD = 5
// px the scrolling element can be stretched past either end at most
const DRAG_OVERSCROLL_MAX = 80
// px an item has to be dragged past for the release to go on to the next one
const DRAG_SWITCH_DISTANCE = 50
// elements with their own mouse interactions (typing, selecting, scrubbing)
const DRAG_IGNORE_SELECTOR = `input, textarea, select, [contenteditable], video, audio`

export const dragScrollActions = (target) =>
  [
    { name: `dragStart`, type: `pointerdown` },
    { name: `dragMove`, type: `pointermove` },
    { name: `dragEnd`, type: `pointerup` },
    { name: `dragEnd`, type: `pointercancel` },
    { name: `dragPress`, type: `mousedown` },
    { name: `dragClick`, type: `click`, capture: true },
    { name: `dragScrolled`, type: `scroll`, passive: true, debounce: 100 },
  ].map((action) => (target ? { ...action, target } : action))

export const dragScroll = {
  drag: null,
  dragSnapping: false,
  dragClickSuppressed: false,

  dragStartAction(e) {
    // a drag that ended without a click must not swallow this press's click
    this.dragClickSuppressed = false

    const scroller = this.dragScroller()
    if (e.pointerType !== `mouse` || e.button !== 0) return
    if (scroller.scrollWidth <= scroller.clientWidth) return
    if (e.target.closest(DRAG_IGNORE_SELECTOR)) return

    this.drag = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: scroller.scrollLeft,
      active: false,
    }
  },

  dragMoveAction(e) {
    if (!this.drag || e.pointerId !== this.drag.pointerId) return

    // released outside the scrolling element before the drag started
    if (!(e.buttons & 1)) {
      this.drag = null
      return
    }

    const scroller = this.dragScroller()
    const deltaX = e.clientX - this.drag.startX

    if (!this.drag.active) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD) return

      this.drag.active = true
      this.dragSnapping = false
      scroller.setPointerCapture(e.pointerId)
      this.dragClassRemove(this.dragStateClass(`snapping`))
      this.dragClassAdd(this.dragStateClass(`dragging`))
    }

    const left = this.drag.startScrollLeft - deltaX
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
    const clampedLeft = Math.min(Math.max(left, 0), maxScrollLeft)

    // `instant`: smooth scrolling (from CSS) would lag behind the mouse
    scroller.scrollTo({ left: clampedLeft, behavior: `instant` })
    this.dragOverscroll(clampedLeft - left)
  },

  // the element can't scroll past its ends, so dragging further shifts it
  // instead, with growing resistance; `excess` is > 0 past the start
  dragOverscroll(excess) {
    const scroller = this.dragScroller()

    if (!excess) {
      scroller.style.removeProperty(`--overscroll`)
      return
    }

    const offset =
      Math.sign(excess) *
      DRAG_OVERSCROLL_MAX *
      (1 - Math.exp(-Math.abs(excess) / (2 * DRAG_OVERSCROLL_MAX)))

    scroller.style.setProperty(`--overscroll`, `${offset}px`)
  },

  dragEndAction(e) {
    if (!this.drag || e.pointerId !== this.drag.pointerId) return

    const { active, startScrollLeft } = this.drag
    this.drag = null
    if (!active) return

    const scroller = this.dragScroller()
    this.dragClickSuppressed = true
    if (scroller.hasPointerCapture(e.pointerId))
      scroller.releasePointerCapture(e.pointerId)

    // in the same style change as the class switch, which turns the transition
    // back on, so a stretched element eases back
    this.dragClassRemove(this.dragStateClass(`dragging`))
    this.dragClassAdd(this.dragStateClass(`snapping`))
    scroller.style.removeProperty(`--overscroll`)

    this.dragSnap(startScrollLeft)
  },

  // the click ending a drag must not open the item it was released on
  dragClickAction(e) {
    if (!this.dragClickSuppressed) return

    this.dragClickSuppressed = false
    e.preventDefault()
    e.stopPropagation()
  },

  // text selection (which auto-scrolls the element towards the cursor) and
  // native link/image dragging would fight the drag, so the press’s default
  // action is cancelled; `pointerdown` fires first and has set `this.drag`
  dragPressAction(e) {
    if (this.drag) e.preventDefault()
  },

  dragScrolledAction() {
    if (this.dragSnapping) this.dragSnapEnded()
  },

  // snapping is off while dragging (it fights manual scrolling), so scroll to
  // an item first and turn it back on once that scroll has ended
  dragSnap(startScrollLeft) {
    const scroller = this.dragScroller()
    const { scrollLeft, scrollWidth, clientWidth } = scroller
    // items snap with their border box start to the inner edge of the element
    const snapStart =
      scroller.getBoundingClientRect().left + scroller.clientLeft
    const maxScrollLeft = scrollWidth - clientWidth
    const positions = [
      ...new Set(
        this.dragItems().map((item) =>
          Math.min(
            Math.max(
              scrollLeft + item.getBoundingClientRect().left - snapStart,
              0
            ),
            maxScrollLeft
          )
        )
      ),
    ].sort((a, b) => a - b)

    const target = this.dragSnapTarget(
      positions,
      scrollLeft,
      Math.sign(scrollLeft - startScrollLeft)
    )

    if (target === undefined || Math.abs(target - scrollLeft) < 1) {
      this.dragSnapEnded()
      return
    }

    this.dragSnapping = true
    // smooth or not as set in CSS, like `scrollIntoView` without `behavior`
    scroller.scrollTo({ left: target })
  },

  // the item the drag went past by `DRAG_SWITCH_DISTANCE` in the drag's
  // `direction` (1 forward, -1 back), so a short drag is enough to switch;
  // otherwise the one it left (or the nearest one, without a direction)
  dragSnapTarget(positions, scrollLeft, direction) {
    const before = positions.filter((left) => left <= scrollLeft).at(-1)
    const after = positions.find((left) => left >= scrollLeft)

    if (before === undefined) return after
    if (after === undefined) return before

    if (direction > 0)
      return scrollLeft - before >= DRAG_SWITCH_DISTANCE ? after : before
    if (direction < 0)
      return after - scrollLeft >= DRAG_SWITCH_DISTANCE ? before : after

    return scrollLeft - before <= after - scrollLeft ? before : after
  },

  dragSnapEnded() {
    this.dragSnapping = false
    this.dragClassRemove(this.dragStateClass(`snapping`))
  },

  // `classAdd` / `classRemove` keep the change through LiveView patches but
  // only apply it on the next frame, which would leave snapping on for the
  // first moves of a drag, so the class list is changed right away as well
  dragClassAdd(className) {
    const scroller = this.dragScroller()
    scroller.classList.add(className)
    this.classAdd(className, scroller)
  },

  dragClassRemove(className) {
    const scroller = this.dragScroller()
    scroller.classList.remove(className)
    this.classRemove(className, scroller)
  },
}
